import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { DeductibleType } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { TriggerCategory } from '@/modules/deductibles/deductibles.types';

export const QUERY_KEY = 'GET_DEDUCTIVLES';
export const NO_DEADLINE_LABEL = 'No deadline';
export const NOT_AVAILABLE_LABEL = 'No available yet';

interface Deductible {
  tier_id: number;
  accumulated: number;
  deductible_target: number;
  next_tier_id: number;
  employee_id: number | null;
  dependent_id: number | null;
  deductible_type: DeductibleType;
}

interface Trigger {
  threshold_amount?: number;
  type: DeductibleType;
  trigger_category_type?: TriggerCategory;
}

interface Tier {
  id: number;
  name: string;
  next_tier_id: number;
  valid_from: string;
  valid_to: string;
  triggers: Trigger[];
}

interface DeductibleListPayload {
  deductibles: Deductible[];
  tiers: Tier[];
  health_plan_id: number;
  health_plan_name: string;
  coverage_level: string;
}

export default (employeeId?: string, serviceOn?: string, dependentId?: string, isIndividual?: boolean) => {
  const { isLoading, data } = useQuery(
    [QUERY_KEY, employeeId, serviceOn, dependentId, isIndividual],
    () =>
      // TODO: types are not matching
      api.get<DeductibleListPayload>(API_URL.TIERS_DEDUCTIBLES, {
        params: {
          employee_id: employeeId,
          deductible_type: isIndividual ? DeductibleType.Individual : DeductibleType.Family,
          service_on: dayjs(serviceOn).format(RAW_DATE_FORMAT),
          ...(dependentId && employeeId !== dependentId && { dependent_id: dependentId }),
        },
      }),
    {
      cacheTime: 0,
      enabled: Boolean(employeeId && serviceOn),
    }
  );

  const healthPlanId = useMemo(() => _.toString(data?.data?.health_plan_id), [data]);
  const healthPlanName = useMemo(() => data?.data?.health_plan_name || '', [data]);
  const coverageLevel = useMemo(() => data?.data?.coverage_level || '', [data]);

  const formattedDeductiblesData = useMemo(() => {
    const list = data?.data?.deductibles || [];
    return list.map((deductible) => ({
      accumulated: deductible.accumulated,
      type: deductible.deductible_type,
      employeeId: deductible.employee_id ? `${deductible.employee_id}` : null,
      dependentId: deductible.dependent_id ? `${deductible.dependent_id}` : null,
    }));
  }, [data]);

  const formattedTiersData = useMemo(() => {
    const list = data?.data?.tiers || [];
    return list.map((item) => ({
      id: `${item.id}`,
      name: item.name || '',
      validFrom: item?.valid_from ? dayjs(item.valid_from).format(DEFAULT_DATE_FORMAT) : undefined,
      validTo: item.valid_to
        ? dayjs(item.valid_to).format(DEFAULT_DATE_FORMAT)
        : item.valid_from
          ? NO_DEADLINE_LABEL
          : NOT_AVAILABLE_LABEL,
      triggers: item?.triggers?.map((trigger) => ({
        type: trigger.type as unknown as DeductibleType,
        threshold: trigger.threshold_amount,
        triggerCategoryType: trigger.trigger_category_type,
      })),
    }));
  }, [data]);

  return {
    isLoading,
    healthPlanId,
    healthPlanName,
    formattedDeductiblesData,
    formattedTiersData,
    coverageLevel,
  };
};
